import { useEffect, useMemo, useState } from "react";
import { ArrowSquareOut, MagnifyingGlass, X } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import {
  Avatar,
  AvatarFallback,
  Button,
  DataRow,
  DataTable,
  Input,
  LoadingOverlay,
  PaginationFooter,
  SearchField,
  buildPageList,
  initialsForMember,
} from "@breakingthelines/design-system";

import { useBodyScrollLock } from "@/hooks/use-body-scroll-lock";
import { grantAdmin, listAdmins, listUsers, revokeAdmin } from "@/lib/admin-api";

import styles from "./admins-page.module.css";

const PER_PAGE_OPTIONS = [10, 20, 50];

function useDebouncedValue<T>(value: T, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(handle);
  }, [value, delay]);

  return debounced;
}

function formatDate(value?: string) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);
}

export function AdminsPage() {
  const [searchInput, setSearchInput] = useState("");
  const debouncedQuery = useDebouncedValue(searchInput.trim());
  const [query, setQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(PER_PAGE_OPTIONS[0]);
  const [reloadKey, setReloadKey] = useState(0);
  const [admins, setAdmins] = useState<Array<ReturnType<typeof mapAdmin>>>([]);
  const [pageInfo, setPageInfo] = useState<{ totalCount?: number; hasNextPage?: boolean } | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [granting, setGranting] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newAdminUsername, setNewAdminUsername] = useState("");
  const [newAdminUserId, setNewAdminUserId] = useState<string | undefined>();
  const [userSuggestions, setUserSuggestions] = useState<
    Array<{ id: string; username: string; displayName?: string }>
  >([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const debouncedUsernameQuery = useDebouncedValue(newAdminUsername.trim(), 250);

  useEffect(() => {
    setQuery(debouncedQuery);
    setPageIndex(1);
  }, [debouncedQuery]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    listAdmins({ query, page: pageIndex, pageSize })
      .then((data) => {
        if (cancelled) return;
        setAdmins((data.users ?? []).map(mapAdmin));
        setPageInfo(data.pageInfo ?? null);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message ?? "Failed to load admins");
        setAdmins([]);
        setPageInfo(null);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [query, pageIndex, pageSize, reloadKey]);

  const totalLabel = useMemo(() => {
    if (pageInfo?.totalCount && pageInfo.totalCount > 0) {
      return `Total Admins: ${pageInfo.totalCount}`;
    }
    return `Total Admins: ${admins.length}`;
  }, [admins.length, pageInfo]);

  const totalPages = useMemo(() => {
    if (pageInfo?.totalCount && pageInfo.totalCount > 0) {
      return Math.max(1, Math.ceil(pageInfo.totalCount / pageSize));
    }
    return pageIndex;
  }, [pageInfo?.totalCount, pageIndex, pageSize]);

  const pages = useMemo(() => buildPageList(totalPages, pageIndex), [totalPages, pageIndex]);
  const activePage = Math.min(pageIndex, totalPages);
  const hasPrevious = pageIndex > 1;
  const hasNext = pageInfo?.hasNextPage ?? (admins.length === pageSize && admins.length > 0);

  function closeCreateDialog() {
    if (granting) return;
    setCreateDialogOpen(false);
    setNewAdminUsername("");
    setNewAdminUserId(undefined);
    setUserSuggestions([]);
    setActionError(null);
  }

  // Shared with the shell's nav drawer lock: a plain save/restore of
  // `document.body.style.overflow` here would clobber the drawer's lock (or be
  // clobbered by it) if both happen to be open at once. See use-body-scroll-lock.
  useBodyScrollLock(createDialogOpen);

  useEffect(() => {
    if (!createDialogOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        closeCreateDialog();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [createDialogOpen, granting]);

  useEffect(() => {
    if (!createDialogOpen) return;

    const normalizedQuery = debouncedUsernameQuery.replace(/^@/, "");
    if (normalizedQuery.length < 2) {
      setUserSuggestions([]);
      setLoadingSuggestions(false);
      return;
    }

    let cancelled = false;
    setLoadingSuggestions(true);

    listUsers({ query: normalizedQuery, pageSize: 8, page: 0 })
      .then((data) => {
        if (cancelled) return;
        const suggestions = (data.users ?? [])
          .filter((user) => !!user.id && !!user.username)
          .map((user) => ({
            id: user.id,
            username: user.username!,
            displayName: user.displayName,
          }));
        setUserSuggestions(suggestions);

        const exact = suggestions.find(
          (user) => user.username.toLowerCase() === normalizedQuery.toLowerCase(),
        );
        if (exact) {
          setNewAdminUserId(exact.id);
          if (newAdminUsername.startsWith("@")) {
            setNewAdminUsername(exact.username);
          }
        }
      })
      .catch(() => {
        if (cancelled) return;
        setUserSuggestions([]);
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingSuggestions(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [createDialogOpen, debouncedUsernameQuery, newAdminUsername]);

  async function handleGrantAdmin() {
    const username = newAdminUsername.trim().replace(/^@/, "");
    if (!username) {
      setActionError("Username is required");
      return;
    }

    setGranting(true);
    setActionError(null);
    try {
      await grantAdmin({ userId: newAdminUserId, username });
      setCreateDialogOpen(false);
      setNewAdminUsername("");
      setNewAdminUserId(undefined);
      setUserSuggestions([]);
      setActionError(null);
      setReloadKey((value) => value + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to grant admin role";
      setActionError(message);
    } finally {
      setGranting(false);
    }
  }

  async function handleRevoke(userId: string) {
    if (!window.confirm("Are you sure you want to revoke admin access for this user?")) return;
    setRevokingId(userId);
    setActionError(null);
    try {
      await revokeAdmin({ userId });
      setReloadKey((value) => value + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to revoke admin role";
      setActionError(message);
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <div className={styles.page}>
      <div>
        <h1 className={styles.title}>Admins</h1>
        <div className={styles.toolbar}>
          <SearchField
            className={styles.searchField}
            icon={<MagnifyingGlass size={14} />}
            label="Search admins"
            placeholder="Search admins"
            value={searchInput}
            onChange={(event) => setSearchInput(event.currentTarget.value)}
          />
          <button
            type="button"
            className={styles.createButton}
            disabled={granting}
            onClick={() => {
              setNewAdminUsername("");
              setNewAdminUserId(undefined);
              setUserSuggestions([]);
              setActionError(null);
              setCreateDialogOpen(true);
            }}
          >
            Create Admin
          </button>
        </div>
      </div>

      {createDialogOpen ? (
        <div className={styles.createDialogOverlay} onMouseDown={() => closeCreateDialog()}>
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="grant-admin-title"
            aria-describedby="grant-admin-description"
            className={styles.createDialogContent}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className={styles.createDialogHeader}>
              <div className={styles.createDialogHeading}>
                <h2 id="grant-admin-title" className={styles.createDialogTitle}>
                  Grant Admin Role
                </h2>
                <p id="grant-admin-description" className={styles.createDialogDescription}>
                  Enter an existing username to grant platform admin access.
                </p>
              </div>
              <button
                type="button"
                className={styles.createDialogClose}
                onClick={() => closeCreateDialog()}
                aria-label="Close"
                disabled={granting}
              >
                <X size={14} weight="bold" />
              </button>
            </header>

            <div className={styles.createDialogBody}>
              <form
                className={styles.createForm}
                onSubmit={(event) => {
                  event.preventDefault();
                  void handleGrantAdmin();
                }}
              >
                <label htmlFor="admin-username" className={styles.createFieldLabel}>
                  Username
                </label>
                <Input
                  id="admin-username"
                  value={newAdminUsername}
                  onChange={(event) => {
                    setNewAdminUsername(event.currentTarget.value);
                    setNewAdminUserId(undefined);
                    if (actionError) {
                      setActionError(null);
                    }
                  }}
                  className={styles.createFieldInput}
                  autoFocus
                  list="admin-username-options"
                  autoComplete="username"
                />
                <datalist id="admin-username-options">
                  {userSuggestions.map((user) => (
                    <option
                      key={user.id}
                      value={user.username}
                      label={
                        user.displayName ? `${user.displayName} (@${user.username})` : undefined
                      }
                    />
                  ))}
                </datalist>
                {loadingSuggestions ? (
                  <p className={styles.createFieldHint}>Searching users...</p>
                ) : null}
                {actionError ? (
                  <p className={styles.createFieldError} role="alert">
                    {actionError}
                  </p>
                ) : null}
              </form>
            </div>

            <footer className={styles.createDialogFooter}>
              <Button
                type="button"
                variant="secondary"
                disabled={granting}
                onClick={() => closeCreateDialog()}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={granting || newAdminUsername.trim().length === 0}
                onClick={() => void handleGrantAdmin()}
              >
                {granting ? "Granting…" : "Grant Admin"}
              </Button>
            </footer>
          </section>
        </div>
      ) : null}

      <section className={styles.tableSection}>
        {loading ? <LoadingOverlay label="Loading admins..." /> : null}
        <DataTable
          columns="minmax(180px, 1.2fr) minmax(110px, 0.8fr) minmax(140px, 0.9fr) minmax(120px, 0.8fr) minmax(150px, 0.9fr)"
          header={["Name", "Role", "Created Date", "Status", "Actions"]}
          headerClassName={styles.tableHeader}
        >
          {admins.map((admin) => (
            <DataRow key={admin.id} className={styles.tableRow}>
              <div className={styles.createdBy}>
                <Avatar size="sm" className={`size-5 ${styles.avatar}`} aria-hidden="true">
                  <AvatarFallback className="text-[10px]">
                    {initialsForMember(admin.initials)}
                  </AvatarFallback>
                </Avatar>
                {admin.handle !== "unknown" ? (
                  <Link to={`/users/${admin.handle}`} className={styles.userLink}>
                    {admin.username}
                  </Link>
                ) : (
                  <span className={styles.meta}>{admin.username}</span>
                )}
              </div>
              <span className={styles.meta} data-label="Role">
                Admin
              </span>
              <span className={styles.meta} data-label="Created Date">
                {admin.createdDate}
              </span>
              <span className={styles.meta} data-label="Status">
                {admin.status}
              </span>
              <div className={styles.actions}>
                <Button
                  variant="link"
                  size="sm"
                  className={styles.viewLink}
                  render={<Link to={`/users/${admin.handle}`} />}
                >
                  View
                  <ArrowSquareOut size={14} />
                </Button>
                <button
                  type="button"
                  className={styles.revokeButton}
                  disabled={revokingId === admin.id}
                  onClick={() => void handleRevoke(admin.id)}
                >
                  {revokingId === admin.id ? "Revoking..." : "Revoke"}
                </button>
              </div>
            </DataRow>
          ))}
        </DataTable>

        {!loading && error ? <div className={styles.emptyState}>{error}</div> : null}
        {!loading && !error && admins.length === 0 ? (
          <div className={styles.emptyState}>No admins found.</div>
        ) : null}
      </section>

      <PaginationFooter
        totalLabel={totalLabel}
        pages={pages}
        activePage={activePage}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        onPrevious={() => setPageIndex((page) => Math.max(1, page - 1))}
        onNext={() => setPageIndex((page) => page + 1)}
        perPage={pageSize}
        perPageOptions={PER_PAGE_OPTIONS}
        onPerPageChange={(option) => {
          setPageSize(option);
          setPageIndex(1);
        }}
        onPageChange={(page) => {
          if (page === activePage) return;
          setPageIndex(page);
        }}
        className={styles.tableFooter}
      />
    </div>
  );
}

function mapAdmin(user: {
  id?: string;
  username?: string;
  displayName?: string;
  createdAt?: string;
  bannedAt?: string | null;
}) {
  const handle = user.username || user.id || "unknown";
  return {
    id: user.id ?? handle,
    handle,
    username: user.username ? `@${user.username}` : "—",
    createdDate: formatDate(user.createdAt),
    status: user.bannedAt ? "Banned" : "Active",
    initials: user.displayName ?? user.username ?? "",
  };
}
